import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { HTTPError } from '~/services/kyClient';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNoteAttachmentAdmin } from './fetchDeliveryNoteAttachmentAdmin';
import { type DeliveryNoteAttachmentAdminPayload } from './types';

/**
 * React Query based custom hook for getting an attachment for a delivery note.
 * @param chainId - The ID of the delivery note chain.
 * @param digest - The digest of the attachment.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/get_delivery_note_attachment_vadmin_vadmin_delivery_note__id__attachment_get
 */
export const useQueryDeliveryNoteAttachmentAdmin = <
  TSelected = Blob | Record<string, unknown> | string,
>(
  { deliveryNoteChainId, digest, name }: DeliveryNoteAttachmentAdminPayload,
  options?: Omit<
    UseQueryOptions<Blob | Record<string, unknown> | string, Error, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    enabled: Boolean(deliveryNoteChainId) && (Boolean(digest) || Boolean(name)),
    gcTime: ms('10m'),
    queryFn: async ({ signal }) =>
      fetchDeliveryNoteAttachmentAdmin(
        deliveryNoteChainId,
        { digest, name },
        { signal },
      ),
    queryKey: queryKeysDeliveryNote.getAttachment({
      deliveryNoteChainId,
      digest,
      name,
    }),
    retry(failureCount, error) {
      if (
        error instanceof HTTPError &&
        [404].includes(error?.response?.status)
      ) {
        // Don't retry if the attachment is not found - it's likely a permission issue
        return false;
      }

      // Retry up to 5 times if needed
      return failureCount <= 5;
    },
    staleTime: ms('1h'),
    ...options,
  });
